import posthog from "posthog-js";
import {isInitialized} from "~/applicationAnalytics/core/init";

// ── Module-level state (set by index.ts during init) ─────────────────────────

let _projectId: string | undefined;
let _clientId: string | undefined;
let _debug = false;

export type WorkspaceContext = {
    workspaceId: string;
    workspaceName: string;
};

export type PageContext = {
    pageName: string;
    pageId?: string;
    pageType?: string;
};

export type PageLoadMetrics = {
    page_load_time_ms: number;
    load_type: "initial_load" | "client_navigation";
    path?: string;
    ttfb_ms?: number;
    dom_interactive_ms?: number;
};

let _workspaceContext: WorkspaceContext | null = null;
let _roleContext: string | null = null;
let _pageContext: PageContext | null = null;
let _isInternal: boolean | null = null;
const MAX_PENDING_CAPTURES = 100;
let _pendingCaptures: Array<{eventName: string; properties: Record<string, unknown>}> = [];

const SUPER_PROPERTY_KEYS = ["workspace_id", "workspace_name", "role", "page_name", "page_id", "page_type", "is_internal"] as const;

/**
 * Configure the capture context. Called once from Argus.init().
 */
export function setCaptureContext(opts: {projectId?: string; clientId?: string; debug?: boolean}): void {
    _projectId = opts.projectId;
    _clientId = opts.clientId;
    _debug = opts.debug ?? false;
}

export function setWorkspaceContext(workspaceContext: WorkspaceContext): void {
    _workspaceContext = {
        workspaceId: workspaceContext.workspaceId,
        workspaceName: workspaceContext.workspaceName,
    };
    syncRegisteredSuperProperties();
}

export function clearWorkspaceContext(workspaceId?: string): void {
    if (workspaceId !== undefined && _workspaceContext !== null && _workspaceContext.workspaceId !== workspaceId) {
        return;
    }
    _workspaceContext = null;
    _roleContext = null;
    syncRegisteredSuperProperties();
}

export function setRoleContext(role: string | null): void {
    _roleContext = role;
    syncRegisteredSuperProperties();
}

export function setIsInternal(isInternal: boolean): void {
    _isInternal = isInternal;
    syncRegisteredSuperProperties();
}

export function setPageContext(pageContext: PageContext | null): void {
    if (pageContext === null) {
        _pageContext = null;
        syncRegisteredSuperProperties();
        return;
    }
    _pageContext = {
        pageName: pageContext.pageName,
        pageId: pageContext.pageId,
        pageType: pageContext.pageType,
    };
    syncRegisteredSuperProperties();
}

export function recordPageLoad(metrics: PageLoadMetrics): void {
    const properties: Record<string, unknown> = {
        page_load_time_ms: metrics.page_load_time_ms,
        load_type: metrics.load_type,
    };
    if (metrics.path !== undefined) {
        properties.path = metrics.path;
    }
    if (metrics.ttfb_ms !== undefined) {
        properties.ttfb_ms = metrics.ttfb_ms;
    }
    if (metrics.dom_interactive_ms !== undefined) {
        properties.dom_interactive_ms = metrics.dom_interactive_ms;
    }
    capture("page_loaded", properties);
}

function contextProperties(): Record<string, unknown> {
    const properties: Record<string, unknown> = {};
    if (_workspaceContext !== null) {
        properties.workspace_id = _workspaceContext.workspaceId;
        properties.workspace_name = _workspaceContext.workspaceName;
    }
    if (_roleContext !== null) {
        properties.role = _roleContext;
    }
    if (_isInternal !== null) {
        properties.is_internal = _isInternal;
    }
    if (_pageContext !== null) {
        properties.page_name = _pageContext.pageName;
        if (_pageContext.pageId !== undefined) {
            properties.page_id = _pageContext.pageId;
        }
        if (_pageContext.pageType !== undefined) {
            properties.page_type = _pageContext.pageType;
        }
    }
    return properties;
}

export function syncRegisteredSuperProperties(): void {
    if (typeof window === "undefined") {
        return;
    }
    try {
        const properties = contextProperties();
        if (Object.keys(properties).length > 0) {
            posthog.register(properties);
        }
        for (const key of SUPER_PROPERTY_KEYS) {
            if (properties[key] === undefined) {
                posthog.unregister(key);
            }
        }
    } catch {
        // PostHog may not be initialized yet — module state still applies on capture().
    }
}

// ── Session ID (persisted per browser tab) ───────────────────────────────────

function getSessionId(): string {
    const KEY = "__argus_session_id";
    try {
        let id = sessionStorage.getItem(KEY);
        if (!id) {
            id = generateId();
            sessionStorage.setItem(KEY, id);
        }
        return id;
    } catch {
        // sessionStorage not available (SSR, incognito limits) — fallback
        return generateId();
    }
}

function generateId(): string {
    // Compact unique id: timestamp-base36 + random suffix
    return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 9);
}

// ── Screen resolution (computed once) ────────────────────────────────────────

function getScreenResolution(): string {
    try {
        return `${window.screen.width}x${window.screen.height}`;
    } catch {
        return "unknown";
    }
}

// ── Central capture function ─────────────────────────────────────────────────

/**
 * Capture an event with automatic enrichment.
 *
 * ALL modules must use this instead of posthog.capture() directly.
 * This ensures every event carries project_id, client_id, session_id,
 * page context, screen resolution, and timestamp.
 */
export function capture(eventName: string, properties?: Record<string, unknown>): void {
    const enriched: Record<string, unknown> = {
        // ── Argus enrichment (what PostHog does NOT auto-add) ──
        session_id: getSessionId(),
        page_url: window.location.href,
        page_path: window.location.pathname,
        screen_resolution: getScreenResolution(),
        timestamp: new Date().toISOString(),

        ...contextProperties(),

        // ── Event-specific properties (caller can override enrichment) ──
        ...properties,
    };

    // Only add project_id / client_id if configured (avoids undefined keys)
    if (_projectId) {
        enriched.project_id = _projectId;
    }
    if (_clientId) {
        enriched.client_id = _clientId;
    }

    if (_debug) {
        console.log(`%c[Argus] 📡 ${eventName}`, "color:#6366f1;font-weight:bold", enriched);
    }

    if (isInitialized() !== true) {
        // Missing analytics configuration must not grow the queue indefinitely.
        if (_pendingCaptures.length >= MAX_PENDING_CAPTURES) {
            _pendingCaptures.shift();
        }
        const snapshot = {...enriched};
        // PostHog merges registered super properties on delivery. Explicit nulls
        // prevent a later page/workspace from filling gaps in an earlier event.
        for (const key of SUPER_PROPERTY_KEYS) {
            if (!(key in snapshot)) {
                snapshot[key] = null;
            }
        }
        _pendingCaptures.push({
            eventName: eventName,
            properties: snapshot,
        });
        return;
    }

    posthog.capture(eventName, enriched);
}

export function flushPendingCaptures(): void {
    if (isInitialized() !== true) {
        return;
    }
    for (const pending of _pendingCaptures) {
        const properties = {...pending.properties};
        // Keep the event's original path, timestamp and page context. Configuration
        // and user classification may only become available during SDK init.
        if (_projectId !== undefined) {
            properties.project_id = _projectId;
        }
        if (_clientId !== undefined) {
            properties.client_id = _clientId;
        }
        if (_isInternal !== null) {
            properties.is_internal = _isInternal;
        }
        posthog.capture(pending.eventName, properties);
    }
    _pendingCaptures = [];
}
